import type { CoachingConfig } from "@coaching/config";
import { coachMoney, percentText, splitOf, ukMonthOf, type MoneyRow } from "@coaching/money";
import { PRICE_TIERS } from "@coaching/price";
import type { Submission } from "../api";
import { monthName, pounds } from "../format";

type Fees = Pick<CoachingConfig, "storeFeeBps" | "platformFeeBps" | "payoutHoldDays">;

export const moneyRow = (s: Submission): MoneyRow => ({ status: s.status, payment: s.payment ?? null, returnedAt: s.timing.returnedAt?.toMillis() ?? null });

/** The cuts in words, the same sentence wherever a price is shown. */
export function FeesNote({ cfg }: { cfg: Fees }) {
    return (
        <p className="hint-text">
            The App Store or Google Play keeps about {percentText(cfg.storeFeeBps)}, Swana keeps {percentText(cfg.platformFeeBps)} of what is left,
            and the rest goes to your club, which pays you your share as agreed with them. The store fee is an estimate until Apple and Google confirm it each month.
        </p>
    );
}

/** Each price tier split three ways, the coach's own tier marked when they have one. */
export function TierSplits({ cfg, selected }: { cfg: Fees; selected?: number | null }) {
    return (
        <div className="table-scroll"><table className="money-table">
            <thead>
                <tr><th scope="col">Player pays</th><th scope="col">App store fee ({percentText(cfg.storeFeeBps)})</th><th scope="col">Swana ({percentText(cfg.platformFeeBps)})</th><th scope="col">Your club</th></tr>
            </thead>
            <tbody>
                {PRICE_TIERS.map((p) => {
                    const s = splitOf(p, cfg);
                    return (
                        <tr key={p} className={selected === p ? "on" : undefined} aria-current={selected === p ? "true" : undefined}>
                            <td>{pounds(s.grossPence)}</td><td>{pounds(s.storePence)}</td><td>{pounds(s.swanaPence)}</td><td className="strong">{pounds(s.clubPence)}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table></div>
    );
}

/** The dashboard's money: what players paid, what is held and due to the club, refunds, and the month by month. */
export function Earnings({ rows, cfg, now }: { rows: readonly MoneyRow[]; cfg: Fees; now: number }) {
    const money = coachMoney(rows, now, cfg);
    const anyMoney = money.kept + money.refunds.count > 0;

    const thisMonth = ukMonthOf(now);
    const dueNow = money.months.find((m) => m.month === thisMonth);
    const lastMonth = money.months.find((m) => m.month < thisMonth);
    const hold = cfg.payoutHoldDays === 1 ? "1 day" : `${cfg.payoutHoldDays} days`;
    return (
        <section className="dash-section">
            <div className="dash-section-head">
                <h2>Earnings</h2>
                <span className="muted small">Your club's share, before it pays you</span>
            </div>
            {!anyMoney ? (
                <p className="muted small">No paid reviews yet. When a player pays for a review, what they paid and your club's share show here.</p>
            ) : (
                <>
                    <div className="stats">
                        <Figure label="Players paid" value={pounds(money.collected.grossPence)}
                            note={`${money.kept} paid ${money.kept === 1 ? "review" : "reviews"}${money.testPurchases > 0 ? `, ${money.testPurchases} test` : ""}`} />
                        <Figure label="Due to your club" value={pounds(dueNow?.split.clubPence ?? 0)}
                            note={`${monthName(thisMonth)} so far${lastMonth ? `. ${monthName(lastMonth.month)}: ${pounds(lastMonth.split.clubPence)}` : ""}`} />
                        <Figure label="Held" value={pounds(money.held.split.clubPence)}
                            note={money.held.nextReleaseAt !== null ? `${money.held.count} returned, next released ${new Date(money.held.nextReleaseAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : `Released ${hold} after you return a review`} />
                        <Figure label="Refunded" value={pounds(money.refunds.grossPence)} tone={money.refunds.pending > 0 ? "warn" : undefined}
                            note={money.refunds.count === 0 ? "None" : money.refunds.pending > 0 ? `${money.refunds.pending} of ${money.refunds.count} still to be refunded` : `${money.refunds.count} refunded`} />
                    </div>
                    {money.inProgress.count > 0 && (
                        <p className="muted small dash-line">
                            {pounds(money.inProgress.split.clubPence)} more for your club once you return the {money.inProgress.count} paid {money.inProgress.count === 1 ? "review" : "reviews"} in progress.
                        </p>
                    )}
                    {money.months.length > 0 && (
                        <div className="table-scroll"><table className="money-table">
                            <thead>
                                <tr><th scope="col">Paid out for</th><th scope="col">Reviews</th><th scope="col">Players paid</th><th scope="col">App store fee</th><th scope="col">Swana</th><th scope="col">Your club</th></tr>
                            </thead>
                            <tbody>
                                {money.months.map((m) => (
                                    <tr key={m.month}>
                                        <td>{monthName(m.month)}</td><td>{m.reviews}</td><td>{pounds(m.split.grossPence)}</td>
                                        <td>{pounds(m.split.storePence)}</td><td>{pounds(m.split.swanaPence)}</td><td className="strong">{pounds(m.split.clubPence)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    )}
                </>
            )}
            <details className="card money-explain">
                <summary>Where each payment goes</summary>
                <TierSplits cfg={cfg} />
                <FeesNote cfg={cfg} />
                <p className="hint-text">
                    Your club's share of a review is held for {hold} after you return it, in case the player asks the store for a refund, then paid to your club with that month's payout.
                    A review that is declined, expires or goes back late is refunded in full, and nothing is paid for it.
                </p>
            </details>
        </section>
    );
}

function Figure({ label, value, note, tone }: { label: string; value: string; note: string; tone?: "warn" }) {
    return (
        <div className="card stat">
            <span className="stat-label">{label}</span>
            <span className={`stat-value${tone ? ` ${tone}` : ""}`}>{value}</span>
            <span className="stat-note">{note}</span>
        </div>
    );
}
